import React, { useEffect, useRef, useState, useMemo } from "react";
import classes from "./QuizAnswer.module.scss";
import Header from "../../molecules/header/Header";
import QuizHeader from "../../molecules/bar-header/BarHeader";
import { fetchQuiz, QuizContent } from "../../api/QuizApi";
import Quiz from "../../molecules/quiz/Quiz";
import SimpleButton from "../../atoms/simple-button/SimpleButton";
import { postUsersQuiz } from "../../api/UsersQuizApi";
import { useLocation, useSearchParams } from "react-router-dom";
import QuizBadgeResult, {
  QuizBadgeResultData,
} from "../../organism/quiz-badge-result/QuizBadgeResult";
import {
  fetchUsersQuizAnswer,
  UserQuizAnswer,
} from "../../api/UsersQuizAnswerApi";
import ProgressBar from "../../organism/progress-bar/ProgressBar";

const QuizAnswer = () => {
  const [quiz, setQuiz] = useState<QuizContent[] | null>(null);
  const [quizAnswer, setQuizAnswer] = useState<UserQuizAnswer[]>([]); // 空配列で安定化

  // ProgressBar / 画面状態
  const [currentNo, setCurrentNo] = useState(1);
  const [displayNext, setDisplayNext] = useState(false);
  const [quizParams, setQuizParams] = useState<
    { quizNumber: number; isCorrect: boolean }[]
  >([]);
  const [showResult, setShowResult] = useState<"hide" | "badge">("hide");
  const [quizResultData, setQuizResultData] =
    useState<QuizBadgeResultData | null>(null);

  const fetchQuizApi = fetchQuiz();
  const fetchQuizAnswerApi = fetchUsersQuizAnswer();
  const postQuizApi = postUsersQuiz();

  const [searchParams] = useSearchParams();
  const quizId = searchParams.get("quizId");
  const location = useLocation();
  const quizType: string = (location.state as string) ?? "education";

  // StrictMode 二重実行による重複フェッチを抑止
  const lastFetchedIdRef = useRef<string | null>(null);

  const init = () => {
    setCurrentNo(1);
    setDisplayNext(false);
    setQuizParams([]);
  };

  useEffect(() => {
    if (!quizId) return;

    // 同じ quizId での重複フェッチを防ぐ（React 18 StrictMode 対策）
    if (lastFetchedIdRef.current === quizId) return;
    lastFetchedIdRef.current = quizId;

    init();
    fetchQuizApi.fetch({ quizId });
    fetchQuizAnswerApi.fetch({ quizId });
  }, [quizId]);

  useEffect(() => {
    if (showResult === "hide") {
      init();
    }
  }, [showResult]);

  // レスポンスが来たときだけ上書き（ロード中に null を入れない）
  useEffect(() => {
    const q = fetchQuizApi.state?.data?.[0]?.quizList;
    const qa = fetchQuizAnswerApi.state?.data?.[0]?.userQuizAnswerList;
    if (q) setQuiz(q);
    if (qa) setQuizAnswer(qa);
  }, [fetchQuizApi.state?.data, fetchQuizAnswerApi.state?.data]);

  useEffect(() => {
    if (!postQuizApi.state?.data) return;
    setQuizResultData({ ...postQuizApi.state.data[0], quizType });
    setShowResult("badge");
  }, [postQuizApi.state?.data]);

  const setQuizResult = (quizNumber: number, isCorrect: boolean) => {
    setQuizParams((prev) => [...prev, { quizNumber, isCorrect }]);
  };

  // インデックス計算（常にクランプ）
  const length = quiz?.length ?? 0;
  const currIdx = useMemo(
    () => Math.max(0, Math.min(Math.max(length - 1, 0), currentNo - 1)),
    [length, currentNo]
  );

  // 現在表示対象の問題
  const currentItem = useMemo(
    () => (length > 0 ? quiz?.[currIdx] ?? null : null),
    [quiz, currIdx, length]
  );

  // 準備完了の定義を「現在問題が存在する」まで厳密化
  const ready = currentItem != null;

  // クイズ配列が来た/変わった時に currentNo を範囲内にクランプ
  useEffect(() => {
    if (!quiz || length === 0) return;
    if (currentNo < 1) setCurrentNo(1);
    else if (currentNo > length) setCurrentNo(length);
  }, [quiz, length, currentNo]);

  // 過去正解なら「つぎの問題」ボタン表示（安全に）
  useEffect(() => {
    if (!ready) return;
    const alreadyCorrect = quizAnswer?.[currIdx]?.isCorrect === true;
    if (!alreadyCorrect) return;

    setQuizParams((prev) =>
      prev.some((p) => p.quizNumber === currIdx + 1)
        ? prev
        : [...prev, { quizNumber: currIdx + 1, isCorrect: true }]
    );
    setDisplayNext(true);
  }, [ready, quizAnswer, currIdx]);

  // 次へボタン（上限ガード）
  const next = () => {
    if (!quiz) return;
    setCurrentNo((n) => Math.min(n + 1, length));
    setDisplayNext(false);
  };

  const finish = () => {
    if (!quizId || !quiz) return;

    // 画面で答えたもの（＆自動で積んだもの）を Map 化（優先）
    const answeredMap = new Map<number, boolean>(
      quizParams.map((p) => [p.quizNumber, p.isCorrect])
    );

    // 全問分の answerList（未回答は過去結果で埋める、なければ0）
    const answerList = quiz.map((_, idx) => {
      const seq = idx + 1;
      const decided = answeredMap.has(seq)
        ? (answeredMap.get(seq)! ? 1 : 0)
        : (quizAnswer?.[idx]?.isCorrect ? 1 : 0);
      return { isCorrect: decided, quizSequence: seq };
    });

    const totalCorrectAnswer = answerList.filter((a) => a.isCorrect === 1).length;
    postQuizApi.post({
      quizId,
      totalCorrectAnswer,
      answerList,
    });
  };

  const close = () => {
    postQuizApi.reset();
    setShowResult("hide");
  };

  return (
    <>
      {showResult !== "hide" && quizResultData != null ? (
        <QuizBadgeResult
          data={{
            quizId: quizResultData.quizId,
            badgeImageData: quizResultData.badgeImageData,
            badgeTitle: quizResultData.badgeTitle,
            badgeExplanation: quizResultData.badgeExplanation,
            isAcquiredBadgeType: quizResultData.isAcquiredBadgeType,
            supportLink: quizResultData.supportLink,
            quizType,
            preAcquiredPoint: quizResultData.preAcquiredPoint,
            currentAcquiredPoint: quizResultData.currentAcquiredPoint,
            prevBadgeTitle: quizResultData.prevBadgeTitle,
            isAcquiredPoint: quizResultData.isAcquiredPoint,
            acquiredPointOwnerList: quizResultData.acquiredPointOwnerList,
          }}
          close={close}
        />
      ) : (
        <div className={classes.container}>
          <Header />
          <QuizHeader title="◯×クイズ" />

          {/* データ準備できた時だけ描画（ゼロ割・瞬断防止） */}
          {ready && (
            <ProgressBar
              max={length}
              current={Math.min(currentNo, length)}
            />
          )}

          {/* 子も currentItem の存在でガード。キーで確実にリマウント */}
          {ready && currentItem && (
            <Quiz
              key={`${currentItem.quizSequence ?? currIdx}-${currentItem.quizId ?? "q"}-${currIdx + 1}`}
              data={currentItem}
              isCorrect={quizAnswer?.[currIdx]?.isCorrect ?? false}
              index={currIdx + 1}
              total={length}
              submit={(result) => setQuizResult(currIdx + 1, result)}
              next={() => setDisplayNext(true)}
            />
          )}

          {displayNext && (
            <div className={classes.button}>
              {currentNo !== length ? (
                <SimpleButton label="つぎの問題" onClick={next} width="343rem" />
              ) : (
                <SimpleButton label="結果発表" onClick={finish} width="343rem" />
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default QuizAnswer;
